/**
 * Noughts and crosses house rules.
 *
 * The game has a problem no card game on this site has: played properly on a
 * 3×3 grid it is a solved draw, and both players find that out within about six
 * rounds. That is not a flaw to hide — it is the reason the variants below
 * exist, and why every one of them changes the *shape* of the game rather than
 * decorating it.
 *
 * The bot's difficulty levels are the other half of that answer, and they live
 * in `bot.ts`: a hard bot never loses, which is the honest game, and the easier
 * ones blunder at a rate that makes winning possible without making it hollow.
 */
/** Two seats, no more, no fewer. Stated so the lobby has something to read. */
export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 2;
export const MIN_BOARD_SIZE = 3;
export const MAX_BOARD_SIZE = 5;
/**
 * Two in a row would end on the second move, so the shortest line that is
 * actually a game is three.
 */
export const MIN_WIN_LENGTH = 3;
const BASE = {
    boardSize: 3,
    winLength: 3,
    misere: false,
};
export const RULE_PRESETS = [
    {
        id: 'classic',
        name: 'Three in a row',
        description: 'The one off the back of every exercise book. Nine squares, three in a row, and no hidden ' +
            'depth — play it perfectly and it is a draw every time, which is exactly why the first person ' +
            'to slip loses.',
        rules: { ...BASE },
    },
    {
        id: 'open',
        name: 'Four by four',
        description: 'Sixteen squares and still only three in a row to win. Far too much space to defend, so the ' +
            'draws stop and the game turns into spotting two threats at once before the other one does.',
        rules: { ...BASE, boardSize: 4, winLength: 3 },
    },
    {
        id: 'misere',
        name: 'Avoid the line',
        description: 'The same grid, upside down: make three in a row and you lose. The board fills, the safe ' +
            'squares run out, and someone has to go first into the trap.',
        rules: { ...BASE, misere: true },
    },
];
/** The set a fresh game starts with. */
export const DEFAULT_RULES = RULE_PRESETS[0].rules;
/**
 * Coerces untrusted input into a valid ruleset. Mirrors `sanitiseRules` on the
 * three card games: anything unrecognised falls back to the default rather than
 * throwing, so a stale or hostile client can never wedge a game.
 */
export function sanitiseRules(input) {
    const raw = (input ?? {});
    const rules = { ...DEFAULT_RULES };
    if (typeof raw.misere === 'boolean')
        rules.misere = raw.misere;
    if (typeof raw.boardSize === 'number' && Number.isFinite(raw.boardSize)) {
        rules.boardSize = clamp(Math.round(raw.boardSize), MIN_BOARD_SIZE, MAX_BOARD_SIZE);
    }
    // Read after the board, and clamped against it: a win length longer than the
    // grid is a game nobody can ever win, which would hang the lobby on a board
    // that never fills to a draw either.
    if (typeof raw.winLength === 'number' && Number.isFinite(raw.winLength)) {
        rules.winLength = clamp(Math.round(raw.winLength), MIN_WIN_LENGTH, rules.boardSize);
    }
    else {
        rules.winLength = Math.min(rules.winLength, rules.boardSize);
    }
    return rules;
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
//# sourceMappingURL=rules.js.map